/**
 * AI Lyric Assistant
 * AI-powered lyrics generation using GPT-5.2
 */

import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import {
  Mic2,
  Sparkles,
  Copy,
  Check,
  RefreshCw,
  X,
  Loader2,
  Lightbulb,
  FileText,
  Download,
  Music2,
  Heart,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Genre options
const GENRES = [
  { id: 'pop', name: 'Pop', emoji: '🎵' },
  { id: 'rock', name: 'Rock', emoji: '🎸' },
  { id: 'hiphop', name: 'Hip-Hop', emoji: '🎤' },
  { id: 'rnb', name: 'R&B', emoji: '💜' },
  { id: 'country', name: 'Country', emoji: '🤠' },
  { id: 'indie', name: 'Indie', emoji: '🎹' },
  { id: 'folk', name: 'Folk', emoji: '🪕' },
  { id: 'electronic', name: 'Electronic', emoji: '🎧' },
];

// Mood options
const MOODS = [
  { id: 'happy', name: 'Happy', emoji: '😊' },
  { id: 'sad', name: 'Sad', emoji: '😢' },
  { id: 'romantic', name: 'Romantic', emoji: '💕' },
  { id: 'angry', name: 'Angry', emoji: '😠' },
  { id: 'hopeful', name: 'Hopeful', emoji: '🌟' },
  { id: 'nostalgic', name: 'Nostalgic', emoji: '🥹' },
  { id: 'empowering', name: 'Empowering', emoji: '💪' },
  { id: 'melancholic', name: 'Melancholic', emoji: '🥀' },
];

// Theme options
const THEMES = [
  { id: 'love', name: 'Love', icon: '❤️' },
  { id: 'heartbreak', name: 'Heartbreak', icon: '💔' },
  { id: 'life', name: 'Life', icon: '🌱' },
  { id: 'party', name: 'Party', icon: '🎉' },
  { id: 'self-discovery', name: 'Self-Discovery', icon: '🔮' },
  { id: 'friendship', name: 'Friendship', icon: '🤝' },
  { id: 'dreams', name: 'Dreams', icon: '✨' },
  { id: 'rebellion', name: 'Rebellion', icon: '🔥' },
];

// Section types
const SECTIONS = [
  { id: 'verse', name: 'Verse' },
  { id: 'chorus', name: 'Chorus' },
  { id: 'pre-chorus', name: 'Pre-Chorus' },
  { id: 'bridge', name: 'Bridge' },
  { id: 'hook', name: 'Hook' },
  { id: 'outro', name: 'Outro' },
];

// Rhyme schemes
const RHYME_SCHEMES = [
  { id: 'ABAB', name: 'ABAB' },
  { id: 'AABB', name: 'AABB' },
  { id: 'ABCB', name: 'ABCB' },
  { id: 'free', name: 'Free' },
];

// Lyric line component
const LyricLine = ({ line, index }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: index * 0.1 }}
    className="flex items-start gap-3 py-2 border-b border-zinc-800/50 last:border-0"
  >
    <span className="text-xs text-zinc-500 font-mono w-6">{line.line_number || index + 1}</span>
    <p className="text-white flex-1">{line.text}</p>
    <span className="text-xs text-zinc-500">{line.syllables} syl</span>
  </motion.div>
);

export const AILyricAssistant = ({
  isOpen,
  onClose,
  onInsertLyrics, // Callback to insert lyrics into project
}) => {
  // State
  const [genre, setGenre] = useState('pop');
  const [mood, setMood] = useState('happy');
  const [theme, setTheme] = useState('love');
  const [section, setSection] = useState('verse');
  const [rhymeScheme, setRhymeScheme] = useState('ABAB');
  const [lines, setLines] = useState(4);
  const [topicKeywords, setTopicKeywords] = useState('');
  const [existingLyrics, setExistingLyrics] = useState('');
  
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Generate lyrics
  const generateLyrics = async () => {
    setIsGenerating(true);
    setResult(null);
    
    try {
      const response = await fetch(`${API_URL}/api/ai-assistant/generate-lyrics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          genre,
          mood,
          theme,
          section,
          style: 'modern',
          rhyme_scheme: rhymeScheme,
          lines,
          topic_keywords: topicKeywords || null,
          existing_lyrics: existingLyrics || null,
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Generation failed');
      }
      
      const data = await response.json();
      setResult(data.lyrics);
      toast.success('Lyrics generated!');
    } catch (err) {
      console.error('Error generating lyrics:', err);
      toast.error(err.message || 'Failed to generate lyrics');
    } finally {
      setIsGenerating(false);
    }
  };

  // Copy lyrics to clipboard
  const copyLyrics = () => {
    if (!result?.lyrics) return;
    
    const text = result.lyrics.map(l => l.text).join('\n');
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  // Export as text file
  const exportLyrics = () => {
    if (!result?.lyrics) return;
    
    const text = [
      `# ${result.title_suggestion || 'Untitled'}`,
      `## ${section.charAt(0).toUpperCase() + section.slice(1)}`,
      '',
      ...result.lyrics.map(l => l.text),
      '',
      `---`,
      `Genre: ${genre} | Mood: ${mood} | Theme: ${theme}`,
      `Rhyme Scheme: ${result.rhyme_scheme_used || rhymeScheme}`,
    ].join('\n');
    
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${result.title_suggestion || 'lyrics'}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    
    toast.success('Lyrics exported!');
  };

  // Insert into project
  const insertIntoProject = () => {
    if (!result?.lyrics) return;
    
    onInsertLyrics?.({
      section,
      lines: result.lyrics.map(l => l.text),
      title: result.title_suggestion,
      metadata: { genre, mood, theme, rhymeScheme },
    });
    
    toast.success('Lyrics added to project!');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-zinc-900 rounded-2xl border border-zinc-800 w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl"
          onClick={e => e.stopPropagation()}
          data-testid="ai-lyric-assistant"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-zinc-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center">
                <Mic2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">AI Lyric Assistant</h2>
                <p className="text-xs text-zinc-400">Powered by GPT-5.2</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="text-zinc-400 hover:text-white">
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Content */}
          <div className="p-4 overflow-y-auto max-h-[calc(90vh-80px)] space-y-4">
            {/* Main Settings */}
            <div className="grid grid-cols-2 gap-4">
              {/* Genre */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Genre</label>
                <div className="grid grid-cols-4 gap-1">
                  {GENRES.map(g => (
                    <button
                      key={g.id}
                      onClick={() => setGenre(g.id)}
                      className={`p-2 rounded-lg text-center transition-all ${
                        genre === g.id
                          ? 'bg-pink-500/20 border border-pink-500/50'
                          : 'bg-zinc-800 border border-zinc-700 hover:border-zinc-500'
                      }`}
                      title={g.name}
                    >
                      <span className="text-lg">{g.emoji}</span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-zinc-500 mt-1">Selected: {GENRES.find(g => g.id === genre)?.name}</p>
              </div>
              
              {/* Mood */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Mood</label>
                <div className="grid grid-cols-4 gap-1">
                  {MOODS.map(m => (
                    <button
                      key={m.id}
                      onClick={() => setMood(m.id)}
                      className={`p-2 rounded-lg text-center transition-all ${
                        mood === m.id
                          ? 'bg-pink-500/20 border border-pink-500/50'
                          : 'bg-zinc-800 border border-zinc-700 hover:border-zinc-500'
                      }`}
                      title={m.name}
                    >
                      <span className="text-lg">{m.emoji}</span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-zinc-500 mt-1">Selected: {MOODS.find(m => m.id === mood)?.name}</p>
              </div>
            </div>
            
            {/* Theme */}
            <div>
              <label className="block text-sm font-medium text-zinc-300 mb-2">Theme</label>
              <div className="flex flex-wrap gap-1">
                {THEMES.map(t => (
                  <button
                    key={t.id}
                    onClick={() => setTheme(t.id)}
                    className={`px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 transition-all ${
                      theme === t.id
                        ? 'bg-pink-500/20 text-pink-400 border border-pink-500/50'
                        : 'bg-zinc-800 text-zinc-400 hover:text-white border border-zinc-700'
                    }`}
                  >
                    <span>{t.icon}</span>
                    {t.name}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Section & Rhyme Scheme */}
            <div className="grid grid-cols-3 gap-4">
              {/* Section Type */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Section</label>
                <div className="flex flex-wrap gap-1">
                  {SECTIONS.map(s => (
                    <button
                      key={s.id}
                      onClick={() => setSection(s.id)}
                      className={`px-2 py-1 rounded text-xs transition-all ${
                        section === s.id
                          ? 'bg-pink-500 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Rhyme Scheme */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Rhyme</label>
                <div className="flex gap-1">
                  {RHYME_SCHEMES.map(r => (
                    <button
                      key={r.id}
                      onClick={() => setRhymeScheme(r.id)}
                      className={`px-2 py-1 rounded text-xs font-mono transition-all ${
                        rhymeScheme === r.id
                          ? 'bg-pink-500 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {r.name}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Lines */}
              <div>
                <label className="block text-sm font-medium text-zinc-300 mb-2">Lines</label>
                <div className="flex gap-1">
                  {[2, 4, 6, 8].map(l => (
                    <button
                      key={l}
                      onClick={() => setLines(l)}
                      className={`px-3 py-1 rounded text-xs transition-all ${
                        lines === l
                          ? 'bg-pink-500 text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Advanced Options Toggle */}
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-sm text-zinc-400 hover:text-white flex items-center gap-2"
            >
              <Zap className="w-4 h-4" />
              {showAdvanced ? 'Hide' : 'Show'} Advanced Options
            </button>
            
            {/* Advanced Options */}
            <AnimatePresence>
              {showAdvanced && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="space-y-3 overflow-hidden"
                >
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-1">Topic/Keywords (optional)</label>
                    <input
                      type="text"
                      value={topicKeywords}
                      onChange={(e) => setTopicKeywords(e.target.value)}
                      placeholder="e.g., summer nights, dancing under stars..."
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:border-pink-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-1">Existing Lyrics (for context)</label>
                    <textarea
                      value={existingLyrics}
                      onChange={(e) => setExistingLyrics(e.target.value)}
                      placeholder="Paste any existing verses or chorus here..."
                      rows={3}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:border-pink-500 focus:outline-none resize-none"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            
            {/* Generate Button */}
            <Button
              onClick={generateLyrics}
              disabled={isGenerating}
              className="w-full bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 h-12"
              data-testid="generate-lyrics-btn"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Writing lyrics...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Generate Lyrics
                </>
              )}
            </Button>
            
            {/* Results */}
            {result && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="space-y-4"
              >
                {/* Title Suggestion */}
                {result.title_suggestion && (
                  <div className="text-center">
                    <p className="text-xs text-zinc-500 uppercase tracking-wide">Suggested Title</p>
                    <h3 className="text-xl font-bold text-white">&ldquo;{result.title_suggestion}&rdquo;</h3>
                  </div>
                )}
                
                {/* Lyrics Display */}
                <div className="p-4 bg-zinc-800/50 rounded-xl border border-zinc-700">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-pink-400" />
                      <span className="font-medium text-white capitalize">{section}</span>
                      <span className="text-xs text-zinc-500 px-2 py-0.5 bg-zinc-800 rounded">
                        {result.rhyme_scheme_used || rhymeScheme}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={copyLyrics}
                        className="text-zinc-400 hover:text-white h-8 w-8 p-0"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={exportLyrics}
                        className="text-zinc-400 hover:text-white h-8 w-8 p-0"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Lyrics Lines */}
                  <div className="space-y-0">
                    {result.lyrics?.map((line, i) => (
                      <LyricLine key={i} line={line} index={i} />
                    ))}
                  </div>
                </div>
                
                {/* Mood & Theme Notes */}
                {(result.mood_achieved || result.theme_notes) && (
                  <div className="p-4 bg-pink-500/10 border border-pink-500/30 rounded-xl">
                    <div className="flex items-start gap-3">
                      <Lightbulb className="w-5 h-5 text-pink-400 mt-0.5" />
                      <div className="space-y-2">
                        {result.mood_achieved && (
                          <p className="text-sm text-zinc-300">{result.mood_achieved}</p>
                        )}
                        {result.theme_notes && (
                          <p className="text-sm text-zinc-400">{result.theme_notes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Melody Suggestions */}
                {result.melody_suggestions && (
                  <div className="p-3 bg-zinc-800/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Music2 className="w-4 h-4 text-pink-400" />
                      <span className="text-sm font-medium text-white">Melody Tips</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs">
                      <div className="bg-zinc-800/50 p-2 rounded">
                        <p className="text-zinc-500">Tempo</p>
                        <p className="text-zinc-300">{result.melody_suggestions.tempo_range}</p>
                      </div>
                      <div className="bg-zinc-800/50 p-2 rounded">
                        <p className="text-zinc-500">Vocal Style</p>
                        <p className="text-zinc-300">{result.melody_suggestions.vocal_style}</p>
                      </div>
                      <div className="bg-zinc-800/50 p-2 rounded">
                        <p className="text-zinc-500">Key</p>
                        <p className="text-zinc-300">{result.melody_suggestions.key_suggestion}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Variations */}
                {result.variations?.length > 0 && (
                  <div className="p-4 bg-zinc-800/30 rounded-xl">
                    <h4 className="font-medium text-white mb-2 flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 text-pink-400" />
                      Alternative Versions
                    </h4>
                    <div className="space-y-2">
                      {result.variations.map((v, i) => (
                        <div key={i} className="p-2 bg-zinc-800/50 rounded-lg">
                          <p className="text-sm font-medium text-pink-400">{v.name}</p>
                          <p className="text-xs text-zinc-400 mb-1">{v.description}</p>
                          {v.lines && (
                            <div className="text-xs text-zinc-500 italic">
                              {v.lines.slice(0, 2).join(' / ')}...
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Writing Tips */}
                {result.writing_tips?.length > 0 && (
                  <div className="p-3 bg-zinc-800/30 rounded-lg">
                    <p className="text-xs text-zinc-500 mb-1">Writing Tips:</p>
                    <ul className="text-sm text-zinc-400 space-y-1">
                      {result.writing_tips.map((tip, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-pink-400">•</span>
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button
                    onClick={generateLyrics}
                    variant="outline"
                    className="flex-1 border-zinc-700"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Regenerate
                  </Button>
                  <Button
                    onClick={insertIntoProject}
                    className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Add to Project
                  </Button>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AILyricAssistant;
